<script lang="ts" setup>
import type { TripMonitorOrder, TripMonitorOrderNotes, TripMonitorOrderStop } from "@/api/trips/dto/trip-monitor";
import { OrderStopTypeMap } from "@/constants/order";
import { computed, ref } from "vue";
import OrderLink from "@/components/links/OrderLink.vue";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import OrderNote from "@/views/trips/monitor/components/OrderNote.vue";
import { useError } from "@/composables/useError";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";

const api = new API();
const { showAPIError } = useError();
const { t } = useI18n();

const props = defineProps<{
  orders: TripMonitorOrder[];
}>();

async function updateGeneralNote(orderId: string, value: string) {
  const order = props.orders.find((o) => o.id === orderId);

  if (!order || order?.generalNote === value) {
    return;
  }

  const response = await api.orders.patchOrder(orderId, {
    generalNote: value,
    orderStops: order.orderStops.map(() => {
      return {};
    }),
  });

  if (response.errorData) {
    showAPIError(response, t("trip.update-note-failed"));
  }
}

async function updateStopNote(orderId: string, stopId: string, value: string) {
  const order = props.orders.find((o) => o.id === orderId);

  if (!order) {
    return;
  }

  const stop = order.orderStops.find((s) => s.id === stopId);

  if (!stop || stop.note === value) {
    return;
  }

  const orderStops = order.orderStops.map((stop) => (stop.id === stopId ? { note: value } : {}));

  const response = await api.orders.patchOrder(orderId, { orderStops });

  if (response.errorData) {
    showAPIError(response, t("trip.update-note-failed"));
  }
}

function getOrderStopName(orderStop: TripMonitorOrderStop, index: number) {
  return `${OrderStopTypeMap[orderStop.type]}#${index + 1}`;
}

const orderEntries = computed<TripMonitorOrderNotes[]>(() => {
  return props.orders.map((order) => {
    return {
      id: order.id,
      number: order.number,
      generalNote: order.generalNote,
      notes: order.orderStops,
    };
  });
});

const MENU_WIDTH = 1248;
const MENU_OFFSET: [number, number] = [-200, 400];

const isOpen = ref(false);
</script>

<template>
  <v-menu v-model="isOpen" :close-on-content-click="false" :offset="MENU_OFFSET" :width="MENU_WIDTH">
    <template #activator="{ props: menuProps }">
      <v-btn
        class="text-primary ml-5 font-weight-600 font-size-11"
        data-qa="notes-button"
        flat
        prepend-icon="mdi-format-quote-close"
        size="x-small"
        v-bind="menuProps"
        variant="outlined"
      >
        {{ t("trip.notes") }} {{ orders.length > 1 ? `(${orders.length})` : "" }}
      </v-btn>
    </template>

    <v-card class="elevation-12 border position-relative">
      <v-btn
        class="position-absolute"
        density="comfortable"
        icon="mdi-close"
        style="top: 8px; right: 8px"
        variant="text"
        @click="isOpen = false"
      />

      <div class="py-5 px-6">
        <div v-for="(order, orderIndex) of orderEntries" :key="order.id" :class="{ 'mt-6': orderIndex !== 0 }">
          <order-link :order-id="order.id" :order-number="order.number" />

          <div class="mt-3">
            <span class="font-size-14 text-grey-darken-3">{{ t("trip.general-note") }}</span>

            <order-note
              :model-value="order.generalNote"
              class="mt-3"
              @blur="(value) => updateGeneralNote(order.id, value)"
            />
          </div>

          <div class="mt-3">
            <div
              v-for="(orderStop, index) of order.notes"
              :key="orderStop.id"
              :class="{
                'mt-6': index !== 0,
              }"
            >
              <div class="d-flex align-center col-gap-2">
                <route-stop-icon :type="orderStop.type" />
                <span class="font-size-14 text-grey-darken-3">
                  {{ t("trip.stop-note-label", { name: getOrderStopName(orderStop, index) }) }}
                </span>
              </div>

              <order-note
                :model-value="orderStop.note"
                class="mt-3"
                @blur="(value) => updateStopNote(order.id, orderStop.id, value)"
              />
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped></style>
